/**
 * #2635 — after buying more screens from a "Screen Limit Reached" prompt, go back to where the
 * customer was, and take Subscription Plan out of the way of the Back button.
 *
 * The prompt navigates Screens → Subscription Plan (a push), and the wizard pushes `#pricing` on
 * top. Closing the wizard used to leave all of that in history, so on a phone the Back gesture
 * (which people use to close pop-ups like Preview) kept landing on Subscription Plan "by itself".
 *
 * So the prompt records the history position it left from, and the wizard steps BACK to it once
 * the purchase is done. Stepping back (rather than pushing Screens again) drops the Subscription
 * Plan entries from the Back path. React Router's browser history stores that position as
 * `history.state.idx`.
 */
import type { NavigateFunction } from 'react-router';

export interface ReturnAfterPurchase {
  /** Where to land (pathname + search), used when the history position can't be trusted. */
  path: string;
  /** `history.state.idx` of the entry the customer left from. */
  historyIdx?: number;
}

/** React Router's index of the current history entry, if the router recorded one. */
export function readHistoryIdx(): number | undefined {
  const state: unknown = window.history.state;
  if (state && typeof state === 'object' && 'idx' in state && typeof state.idx === 'number') {
    return state.idx;
  }
  return undefined;
}

/** The return target from router state, or undefined when absent or malformed. In-app paths only. */
export function parseReturnAfterPurchase(value: unknown): ReturnAfterPurchase | undefined {
  if (!value || typeof value !== 'object' || !('path' in value)) return undefined;
  const { path } = value;
  if (typeof path !== 'string' || !path.startsWith('/app/')) return undefined;
  const historyIdx =
    'historyIdx' in value && typeof value.historyIdx === 'number' ? value.historyIdx : undefined;
  return { path, historyIdx };
}

/**
 * Step back to the entry the customer left from. Falls back to replacing the current entry with
 * the path when the position is unknown or not behind us (e.g. the page was reloaded in a new
 * tab, so the earlier entries are gone).
 */
export function goBackAfterPurchase(target: ReturnAfterPurchase, navigate: NavigateFunction): void {
  const currentIdx = readHistoryIdx();
  if (
    target.historyIdx !== undefined &&
    currentIdx !== undefined &&
    target.historyIdx < currentIdx
  ) {
    navigate(target.historyIdx - currentIdx);
    return;
  }
  navigate(target.path, { replace: true });
}
